import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Img from 'react-image';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { findIndex, get, isEqual } from 'lodash';
import hash from 'object-hash';
import {
  Box,
  FlexBox,
  Typography,
  color,
  Button,
  Logo,
  Grid,
  MuiBox,
  MuiCardContent,
  textSize
} from '@kis-technologies/kis-ui';
import WillCallLogo from 'assets/will_call_icon.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { TicketCard } from 'components/TicketCard';
import { TicketRow, UniqueOffers } from 'components/TicketRow';
import { Scrollbar } from 'components/Scrollbar';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, actions as cartActions, Cart } from 'services/cart';
import { actions as cityPassActions, selectors as cityPassSelectors, CityPassTicket } from 'services/cityPass';
import {
  selectors as configSelectors,
  Configuration,
  CartRule,
  EventDisplaySettings,
  LanguageField,
  Message,
  Messages
} from 'services/configuration';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { selectors as mediaSelectors, MediaResponse } from 'services/media';
import {
  selectors as ticketingSelectors,
  actions as ticketingActions,
  Event,
  EventDetails,
  Offer,
  GroupOffer,
  UpsellStore
} from 'services/ticketing';
import { formatCurrency, formatText } from 'utils/stringFormat';
import { getEventDisplaySettings } from 'views/Ticketing/utils';
import { useStyles } from './styled';

const TicketSelectionView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { from?: string };

  const { reserveTickets, resetCartError, setWasAutoReserveTriggered, getEventDetails, setEventsConfig, setUpsell } =
    useActions({
      reserveTickets: cartActions.reserveTickets,
      resetCartError: cartActions.resetCartError,
      setWasAutoReserveTriggered: cityPassActions.setWasAutoReserveTriggered,
      getEventDetails: ticketingActions.getEventDetails,
      setEventsConfig: ticketingActions.setEventsConfig,
      setUpsell: ticketingActions.setUpsell
    });
  const eventDetails = useSelector(ticketingSelectors.selectEventDetails) as EventDetails;
  const upsell = useSelector(ticketingSelectors.selectUpsell) as UpsellStore;
  const upsellProducts = useSelector(ticketingSelectors.selectUpsellProducts);
  const tiersConfig = useSelector(ticketingSelectors.selectTiersConfig);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const cartReserving = useSelector(cartSelectors.selectReserving);
  const cartError = useSelector(cartSelectors.selectError);
  const cityPassTickets = useSelector(cityPassSelectors.selectTickets) as CityPassTicket[];
  const wasAutoReserveTriggered = useSelector(cityPassSelectors.selectWasAutoReserveTriggered);
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const loading = useSelector(ticketingSelectors.selectLoading);
  const ticketingError = useSelector(ticketingSelectors.selectError);
  const eventImages = useSelector(mediaSelectors.selectEventImages) || [];
  const promoApplied = useSelector(ticketingSelectors.selectAppliedPriceTypePromo);
  const backgroundImage = useSelector(mediaSelectors.selectBackgroundImage) || [];
  const logoImage = useSelector(mediaSelectors.selectLogoImage) || [];
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const flowTemplate = get(config, 'configuration.flowTemplate');
  const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`, t('cart.cartLabel'));
  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const orderLimit: number = get(config, `${ticketingPath}.orderLimit`, -1);
  const priceTypeLimit = get(config, `${ticketingPath}.priceTypeLimit`);
  const cartEnabled = get(config, `${ticketingPath}.cart.enabled`, false);
  const cartRules: CartRule[] = get(config, `${ticketingPath}.cartRules`, []);
  const eventDisplaySettingsList: EventDisplaySettings[] = get(config, `${ticketingPath}.eventDisplaySettingsList`, []);
  const support = get(config, `${ticketingPath}.supportName.${i18n.language}`) || i18n.t('commonText.support');
  const loadingMessage = get(
    config,
    `messaging.${i18n.language}.reservingTicket.header`,
    i18n.t('ticketingRoute.pleaseHold')
  );
  const messaging: Message = get(config, `messaging.${i18n.language}.ticketSelectionPage`, {});
  const upsellMessages: Messages = get(
    config,
    `${ticketingPath}.productAvailabilityAndSelection.upsell.ticketSelectionMessages`,
    {}
  );
  const upsellHeader = get(upsellMessages, `header.${i18n.language}`, '');
  const upsellSubheader = get(upsellMessages, `subheader.${i18n.language}`, '');
  const upsellBackText = get(upsellMessages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const upsellProceedText = get(
    upsellMessages,
    `buttons.${i18n.language}[1]`,
    cartEnabled ? t('cartSummary.proceedToCart', { cartLabel }) : t('commonText.next')
  );
  const ticketsUnavailable: Message = get(config, `messaging.${i18n.language}.ticketsUnavailable`, {});
  const bgImageName = get(backgroundImage[0], 's3Name') || 'default.png';
  const promoEnabled = get(config, `${ticketingPath}.promoCode.priceType.enabled`, false);
  const promoSelectionEnabled = get(config, `${ticketingPath}.promoCode.selection.enabled`, false);
  const promoMessages = get(config, `${ticketingPath}.promoCode.priceType.messages`, {});
  const promoEnterButton = get(promoMessages, `buttons.${i18n.language}[0]`, t('promoCode.enterButton'));
  const { clientId } = kiosk.venue;
  const imageBaseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}`;
  const previewUrl = `${imageBaseUrl}/event_images/`;
  const backgroundUrl = `${imageBaseUrl}/background_images/${bgImageName}`;
  const palette = get(config, 'configuration.globalSettings.palette');
  const fonts = get(config, 'configuration.globalSettings.fonts');
  const fontFamily = get(fonts, 'primary', '');
  const { showDate, showTime, showLocation, showTier } = getEventDisplaySettings(
    eventDisplaySettingsList,
    eventDetails ? eventDetails.name : ''
  );
  const autoSelectTicketsForCityPass = get(config, 'cityPass.autoSelectTickets', false);

  const isTierSelected = Boolean(tiersConfig && tiersConfig.selected && tiersConfig.selected.tier);

  // state variables
  const [nextDisabled, setNextDisabled] = React.useState<boolean>(true);
  const [nextButtonText, setNextButtonText] = React.useState<string>(t('commonText.next'));
  const [orderLimitExceeded, setOrderLimitExceeded] = React.useState<boolean>(false);
  const [cartRequestBody, setCartRequestBody] = React.useState<any[]>([]);
  const [groupOffers, setGroupOffers] = React.useState<GroupOffer[] | null>(null);
  const [priceTypes, setPriceTypes] = React.useState<Offer[] | null>(null);
  const [cartRuleViolation, setCartRuleViolation] = React.useState<string>('');
  const [uiLoading, setUILoading] = React.useState(true);
  const [sortedEventDates, setSortedEventDates] = React.useState<string[]>([]);
  const [isAutoReserving, setIsAutoReserving] = React.useState<boolean>(false);

  const classes = useStyles({ fontFamily })();

  const backgroundStyle = () => {
    if (config && palette) {
      return { background: `${palette.darkPrimary}e5` };
    }
    return {};
  };

  React.useEffect(() => {
    if (flowTemplate === 'fair' && !eventDetails) {
      getEventDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // control the 'Next' button behavior for the upsell flow
  React.useEffect(() => {
    if (eventDetails && eventDetails.upsell && !eventDetails.upsell.initialProductRequired) {
      // if on the initial upsell product and it is not required
      if (isTierSelected) {
        // if a tier was selected, enable the 'Skip' button
        setNextDisabled(false);
        setNextButtonText(t('commonText.skip'));
      } else {
        // else a tier was not selected, so skip this page
        if (state && state.from === '/ticketing/upsell-selection') {
          onPrevPageClick();
        } else {
          onNextPageClick();
        }
      }
    } else if (eventDetails && !eventDetails.upsell && upsellProducts.length === 1) {
      // if on the only product to upsell
      if (isTierSelected && cart) {
        // if a tier was selected and there is a cart, enable the 'Proceed to cart' button
        setNextDisabled(false);
        setNextButtonText(upsellProceedText);
      } else if (!isTierSelected) {
        // if a tier was not selected, skip this page
        onNextPageClick();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (eventDetails && tiersConfig) {
      let tier = null;
      const eventDates: string[] = [];
      if (eventDetails && eventDetails.offers && eventDetails.offers.length && tiersConfig.selected.tier) {
        tier = eventDetails.offers.filter((offer) => offer.tier === tiersConfig.selected.tier.tier);
      }
      if (eventDetails && eventDetails.groups && eventDetails.groups.length) {
        const selectedGroupOffers: GroupOffer[] = [];
        Object.keys(tiersConfig.selected).forEach((selection) => {
          const selectedGroup =
            eventDetails && eventDetails.groups.find((group) => group.id === tiersConfig.selected[selection].groupId);
          if (selectedGroup) {
            const selectedEvent = selectedGroup.events.find((evt) => evt.id === selection);
            if (selectedEvent) {
              const selectedOffer = selectedEvent.offers.filter(
                (offer) => offer.tier === tiersConfig.selected[selection].tier
              );
              if (selectedOffer && selectedOffer.length) {
                selectedOffer.forEach((ofr) => {
                  selectedGroupOffers.push({ ...ofr, packageGroupId: selectedGroup.id, eventId: selectedEvent.id });
                });
              }
              eventDates.push(selectedEvent.date as string);
            }
          }
        });
        setGroupOffers(selectedGroupOffers);
        setSortedEventDates(eventDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()));
        tier = Object.values(
          selectedGroupOffers.reduce<{ [x: string]: Offer }>((acc, cur) => {
            const { type } = cur;
            if (acc.hasOwnProperty(type)) {
              acc[type].basePrice += cur.basePrice;
              acc[type].availability = Math.min(acc[type].availability, cur.availability);
            } else {
              acc[type] = { ...cur };
            }
            return acc;
          }, {})
        );
      }
      setPriceTypes(tier);
    }
  }, [eventDetails, tiersConfig]);

  React.useEffect(() => {
    if (priceTypes) {
      // Determine if offers should be auto selected based on cityPass tickets
      const autoSelectedOffers = getAutoSelectedOffersForCityPass();
      if (autoSelectedOffers.length > 0) {
        handleAutoSelectForCityPass(autoSelectedOffers);
      } else {
        setUILoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceTypes]);

  // on update of cartReserving - redirect to the next page (UpsellSelection)
  React.useEffect(() => {
    if (cart && !nextDisabled && !cartReserving && !cartError) {
      if (isAutoReserving) setWasAutoReserveTriggered(true);
      goToUpsellSelection();
    } else if (cartReserving) {
      // set uiLoading to keep the loading screen active after the reserve finishes
      setUILoading(true);
    } else if (!cartReserving && cartError) {
      // set uiLoading to false if an error occurred
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartReserving]);

  React.useEffect(() => {
    if (flowTemplate === 'fair' && ticketingError) navigate('/ticketing/attract');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketingError]);

  // redirect to GenresView if an event is not selected
  if (flowTemplate !== 'fair' && !eventDetails) return <Navigate to={`/ticketing/genres`} />;

  const offers = () => {
    if (flowTemplate === 'fair') {
      const offerList = eventDetails
        ? eventDetails.offers
          .sort((a, b) => a.basePrice - b.basePrice)
          .map((offer) => ({
            offerId: `${offer.type}`,
            type: offer.type,
            typeId: offer.typeId,
            typeLimit: offer.typeLimit,
            typePriority: offer.typePriority,
            isPromoCodeOffer: offer.isPromoCodeOffer,
            isCityPassEligible: offer.isCityPassEligible,
            purchaseParams: offer.purchaseParams,
            basePrice: offer.basePrice,
            availability: offer.availability,
            groupId: offer.groupId,
            groupLimit: offer.groupLimit,
            groupPriority: offer.groupPriority
          }))
        : [];

      return offerList;
    }
    const offerList = priceTypes
      ? priceTypes
        .sort((offer) => (offer.isPromoCodeOffer ? -1 : 0))
        .map((offer) => ({
          offerId: `${offer.type}-${offer.tier}`,
          type: offer.type,
          typeId: offer.typeId,
          typeLimit: offer.typeLimit,
          typePriority: offer.typePriority,
          isPromoCodeOffer: offer.isPromoCodeOffer,
          isCityPassEligible: offer.isCityPassEligible,
          purchaseParams: offer.purchaseParams,
          basePrice: offer.basePrice,
          availability: offer.availability,
          groupId: offer.groupId,
          groupLimit: offer.groupLimit,
          groupPriority: offer.groupPriority
        }))
      : [];
    // --sorting offerlist according to typePriority and groupPriority
    offerList.sort((a, b) => a.typePriority - b.typePriority);
    offerList.sort((a, b) => a.groupPriority - b.groupPriority);

    return offerList;
  };

  //* * logic for subtitle count*/
  const ticketAvailability = offers()
    .slice(-1)
    .map((details) => details.availability)
    .shift();

  let orderLimitValue: number | undefined;
  if (ticketAvailability && ticketAvailability >= 0) {
    orderLimitValue = ticketAvailability;
  }
  if (orderLimit > -1 && (!orderLimitValue || orderLimit < orderLimitValue)) {
    orderLimitValue = orderLimit;
  }

  // ***************
  // ** FUNCTIONS **
  // ***************

  const getAutoSelectedOffersForCityPass = () => {
    const autoSelectedOffers: Offer[] = [];

    if (
      priceTypes &&
      priceTypes.length &&
      cityPassTickets &&
      cityPassTickets.length > 0 &&
      autoSelectTicketsForCityPass
    ) {
      // Find offers that match the cityPass ticket classIds
      cityPassTickets.forEach((cityPassTicket) => {
        const matchingOffer = priceTypes.find(
          (pt) => pt.isCityPassEligible && pt.cityPassClassId === cityPassTicket.classId
        );
        if (matchingOffer && matchingOffer.purchaseParams) {
          autoSelectedOffers.push(matchingOffer.purchaseParams);
        }
      });
    }

    return autoSelectedOffers;
  };

  const handleAutoSelectForCityPass = (autoSelectedOffers: Offer[]) => {
    const isCityPassItemInCart =
      cart &&
      cart.products.some((product) => {
        const items = product.subProducts ? product.subProducts.flatMap((sub) => sub.items) : product.items;
        return items?.some((item) => item?.isCityPass);
      });
    if (wasAutoReserveTriggered && state && state.from === '/ticketing/upsell-selection') {
      // Bypass this page if auto reserve was triggered and coming back from upsell selection
      setWasAutoReserveTriggered(false);
      onPrevPageClick();
    } else if (!wasAutoReserveTriggered && !isCityPassItemInCart) {
      // Only auto reserve if we haven't already auto reserved and there isn't a cityPass item already in the cart
      setIsAutoReserving(true);
      setNextDisabled(false);
      reserveTickets(autoSelectedOffers);
    } else {
      // Otherwise just load the page without auto reserving
      setUILoading(false);
    }
  };

  // ticket select => Update ticket quantity
  const updateTicketQuantity = (offer: UniqueOffers, operator: 'add' | 'subtract') => {
    const currentCart = cartRequestBody;
    let newCart: any[];

    if (operator === 'add') {
      newCart = currentCart.concat({
        ...offer.purchaseParams,
        groupId: offer.groupId,
        typeId: offer.typeId
      });
    } else {
      currentCart.splice(
        findIndex(currentCart, { ...offer.purchaseParams, groupId: offer.groupId, typeId: offer.typeId }),
        1
      );
      newCart = [...currentCart];
    }

    if (eventDetails.upsell && !eventDetails.upsell.initialProductRequired) {
      // if on the initial upsell product and it is not required: toggle between 'Skip' and 'Next', always enabled
      setNextButtonText(newCart.length === 0 ? t('commonText.skip') : t('commonText.next'));
    } else if (!eventDetails.upsell && upsellProducts.length === 1 && cart) {
      // if on the only upsell product: toggle between 'Proceed to cart' and 'Next', always enabled
      setNextButtonText(newCart.length === 0 ? upsellProceedText : t('commonText.next'));
    } else {
      // else toggle between enabling and disabling the next button
      setNextDisabled(newCart.length === 0);
    }
    setCartRequestBody(newCart);
  };

  const getPromoCodePath = () => {
    let path = '/ticketing/price-type/promo';
    if (promoSelectionEnabled) {
      path = '/ticketing/promo-code/selection';
    }
    return path;
  };

  const goToUpsellSelection = () => {
    if (upsellProducts.length === 1) {
      // if on the only upsell product, set this flag so it does not select the product again
      setUpsell({ ...upsell, singleUpsellProductOffered: true });
    }
    // reset this config so it starts on page 1
    setEventsConfig({ page: 1, selected: '' });

    navigate('/ticketing/upsell-selection');
  };

  // footer => controls next and previous buttons
  const onPrevPageClick = () => {
    navigate('/ticketing/tiers', { state: { from: location.pathname } });
  };

  const onNextPageClick = () => {
    if (!cartRequestBody || !cartRequestBody.length) {
      // if nothing to reserve, nav forward to upsell selection
      goToUpsellSelection();
    } else if (orderLimit > -1 && cartRequestBody.length > orderLimit) {
      setOrderLimitExceeded(true);
    } else {
      const isCartRuleViolated = validateCartRules();
      // If there is no cart rule violations, continue
      if (!isCartRuleViolated) {
        if (groupOffers && groupOffers.length) {
          const packageOffers = cartRequestBody.filter((item) => item.PackageId);
          packageOffers.forEach((packageOffer) => {
            Object.keys(tiersConfig.selected).forEach((eventId) => {
              const selectedOffer =
                groupOffers &&
                groupOffers.find((gpOffer) => {
                  return (
                    gpOffer.packageGroupId === tiersConfig.selected[eventId].groupId &&
                    gpOffer.eventId === eventId &&
                    gpOffer.tier === tiersConfig.selected[eventId].tier &&
                    gpOffer.purchaseParams.PriceType === packageOffer.PriceType
                  );
                });
              if (selectedOffer) {
                const { groupId, typeId, ...orgPkgOffer } = packageOffer;
                if (isEqual(selectedOffer.purchaseParams, orgPkgOffer)) {
                  const indx = cartRequestBody.findIndex((itm) => isEqual(itm, packageOffer));
                  cartRequestBody.splice(indx, 1, selectedOffer.purchaseParams);
                } else {
                  cartRequestBody.splice(0, 0, selectedOffer.purchaseParams);
                }
              }
            });
          });
        }
        reserveTickets(cartRequestBody);
      }
    }
  };

  const onErrorOverlayConfirm = () => {
    // Reset error to close overlay
    resetCartError();
    if (flowTemplate && flowTemplate === 'fair') {
      // Navigate back to attract if in fair flow
      navigate('/ticketing/attract');
    } else if (isAutoReserving) {
      // Navigate back to previous page if auto reserving for cityPass
      onPrevPageClick();
    }
  };

  const validateCartRules = () => {
    let isCartRuleViolated = false;
    // Validate cartRules if defined in configurations.ticketing
    if (cartRules.length > 0) {
      cartRules.every((rule) => {
        const dependentCartItems = cartRequestBody.filter(
          (cartItem) => rule.dependentIds && rule.dependentIds.includes(cartItem.typeId)
        );
        // Continue if there are dependents in cartRequestBody
        if (dependentCartItems && dependentCartItems.length > 0) {
          // Continue if there are reliesOn defined
          if (rule.reliesOnIds && rule.reliesOnIds.length > 0) {
            const reliesOnCartItems = cartRequestBody.filter((cartItem) => rule.reliesOnIds.includes(cartItem.typeId));
            // Continue if there are no reliesOn in cartRequestBody
            if (reliesOnCartItems && reliesOnCartItems.length < 1) {
              const reliesOnNames = offers()
                .filter((offer) => rule.reliesOnIds.includes(offer.typeId))
                .map((offer) => offer.type);

              let reliesOnString = reliesOnNames && reliesOnNames.toString();
              if (reliesOnNames && reliesOnNames.length > 1) {
                const lastElement = reliesOnNames.pop();
                reliesOnString = `${reliesOnNames.join(', ')} ${t('cartRule.or')} ${lastElement}`;
              }

              setCartRuleViolation(t('cartRule.violationMessage', { reliesOnString }));
              isCartRuleViolated = true;
              return false;
            }
          }
        }
        return true;
      });
    }
    return isCartRuleViolated;
  };

  const addToCart = (offer: UniqueOffers) => {
    setNextDisabled(false);
    setCartRequestBody([offer.purchaseParams]);
    reserveTickets([offer.purchaseParams]);
  };

  const getEventImageUrl = (eventName: string, imageUrl?: string) => {
    const imgObj =
      eventName &&
      eventImages.find((item: MediaResponse) => {
        return item.name === hash(eventName.replace(new RegExp('/', 'g'), ' '));
      });
    // image from media table
    if (imgObj && imgObj.s3Name) {
      return previewUrl.concat(imgObj.s3Name);
    }
    // image from ticketing system
    if (imageUrl) {
      return imageUrl;
    }

    return '';
  };
  // **************
  // **  RENDER  **
  // **************

  const getHeaderText = () => {
    let header = t('ticketSelection.ticketMessagingHeader');

    if (upsellProducts.length && upsellHeader) {
      header = upsellHeader;
    } else if (messaging && messaging.header) {
      header = messaging.header;
    }

    return header;
  };

  const getSubheaderText = () => {
    let message = t('ticketSelection.addTickets');

    if (upsellProducts.length && upsellSubheader) {
      message = upsellSubheader;
    } else if (messaging && messaging.message) {
      message = messaging.message;
    } else if (orderLimit === 1) {
      message = t('ticketSelection.addUpToTickets', { count: orderLimitValue });
    } else if (orderLimit > 1) {
      message = t('ticketSelection.addUpToTickets_plural', { count: orderLimitValue });
    }

    return message;
  };

  const getDateForDisplay = (e: Event) => {
    let displayDate = '';

    if (showDate) {
      if (e.firstDate && e.lastDate && sortedEventDates.length) {
        displayDate = `${t('{{date, weekdayMonthDay}}', { date: new Date(sortedEventDates[0]) })} - ${t(
          '{{date, weekdayMonthDay}}',
          {
            date: new Date(sortedEventDates[sortedEventDates.length - 1])
          }
        )}`;
      } else if (e.date) {
        displayDate = t('{{date, weekdayMonthDay}}', { date: new Date(e.date) });
      }
    }

    return displayDate;
  };

  const getTimeForDisplay = (e: Event) => {
    let displayTime = '';
    if (showTime && e.date) {
      displayTime = t('{{date, time}}', { date: new Date(e.date) });
    }

    return displayTime;
  };

  const isDateAndTimeDisplayed = (e: Event) => {
    const dateDisplayed = showDate && e.date;
    const timeDisplayed = showTime && e.date;

    return dateDisplayed && timeDisplayed;
  };

  const getTierForDisplay = () => {
    let tier = '';
    if (showTier && isTierSelected) {
      tier = tiersConfig.selected.tier.tier;
    }

    return tier;
  };

  const isTierAndLocationDisplayed = (e: Event) => {
    const tierDisplayed = getTierForDisplay();
    const locationDisplayed = showLocation && e.venue;

    return tierDisplayed && locationDisplayed;
  };

  const generateTicketSelectionView = () => {
    if (flowTemplate !== 'fair' && !loading && !cartReserving) {
      return (
        <>
          <Grid className={classes.ticketSelectionContainer}>
            <Grid container item lg={12} xl={6} className={classes.ticketSelectInnerContainer}>
              {/* Card container with event details */}
              <Grid container direction="column" className={classes.ticketImageBlock}>
                {eventDetails !== undefined && (
                  <>
                    <MuiCardContent className={classes.ticketImageCard}>
                      <Img
                        src={getEventImageUrl(eventDetails.name, eventDetails.imageUrl)}
                        className={classes.ticketImage}
                        unloader={<img src={WillCallLogo} alt="Event Logo" className={classes.ticketDefaultImage} />}
                      />
                    </MuiCardContent>
                    <MuiBox className={classes.eventDetailsBlock}>
                      <Typography className={classes.ticketName}>{eventDetails.name}</Typography>
                      <Typography className={classes.ticketDateTime}>
                        {getDateForDisplay(eventDetails)}
                        {isDateAndTimeDisplayed(eventDetails) ? <> &middot; </> : null}
                        {getTimeForDisplay(eventDetails)}
                      </Typography>
                      <Typography className={classes.ticketDateTime}>
                        {getTierForDisplay()}
                        {isTierAndLocationDisplayed(eventDetails) ? <> &middot; </> : null}
                        {showLocation && eventDetails.venue}
                      </Typography>
                      <Typography className={classes.eventDescription}>{eventDetails.description}</Typography>
                    </MuiBox>
                  </>
                )}
              </Grid>
              <Grid container direction="column" className={classes.ticketContentBlock}>
                {/* Header */}
                <FlexBox direction="column" justify="between" align="start" margin="0 0 3">
                  <MuiBox className={classes.ticketHeaderBlock}>
                    <Box flexOverride="none">
                      <Typography className={classes.ticketHeader}>{getHeaderText()}</Typography>
                    </Box>
                    <MuiBox className={classes.ticketSubHeading}>
                      <Typography fontSize={30}>{getSubheaderText()}</Typography>
                      {orderLimitExceeded && (
                        <Typography color={color.RED} fontSize={textSize.kiosk.SMALL}>
                          {t('ticketSelection.exceededLimit', { count: orderLimit })}
                        </Typography>
                      )}
                    </MuiBox>
                  </MuiBox>
                  {promoEnabled && !promoApplied && !cart && (
                    <FlexBox align="start" margin="3 0 0 0">
                      <Button
                        customSize="x-large"
                        appearance="white"
                        onClick={() => navigate(getPromoCodePath(), { state: { from: location.pathname } })}
                      >
                        {promoEnterButton}
                      </Button>
                    </FlexBox>
                  )}
                </FlexBox>
                {/* Price Level Container */}

                <FlexBox sx={{ minHeight: '21.5rem' }} direction="column" gutter={3} noWrap flexOverride="1">
                  {priceTypes && priceTypes.length > 0 && (
                    <Scrollbar>
                      <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                        {/* adds in all the offers */}
                        {offers().map((offer, idx) => (
                          <TicketRow
                            key={idx}
                            index={idx}
                            offer={offer}
                            formattedPrice={formatCurrency(offer.basePrice, {}, languageSettings)}
                            currentCart={cartRequestBody}
                            updateAmount={updateTicketQuantity}
                            ticketLimit={priceTypeLimit || (orderLimit > -1 ? orderLimit : undefined)}
                            orderLimit={orderLimit > -1 ? orderLimit : undefined}
                          />
                        ))}
                      </FlexBox>
                    </Scrollbar>
                  )}
                </FlexBox>

                {/* configurable footer text */}
                {messaging && messaging.footer && (
                  <Grid container direction="row" className={classes.footerTextBlock}>
                    <Typography fontSize={22}>{messaging.footer}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          {/* Footer */}
          <Footer
            onPrevClick={onPrevPageClick}
            onNextClick={onNextPageClick}
            prevText={upsellProducts.length > 0 ? upsellBackText : t('commonText.back')}
            nextText={nextButtonText}
            isNextDisabled={nextDisabled}
            skipNavPrompt={Boolean(cartRuleViolation)}
          />
        </>
      );
    }
    return (
      <>
        {!loading && !cartReserving && (
          <FlexBox height="1920px" overflow="hidden">
            <FlexBox sx={{ width: '1080px', backgroundImage: `url(${backgroundUrl})` }}>
              <FlexBox height="12.5%" fullWidth align="end" justify="center">
                {logoImage && logoImage.length > 0 ? (
                  <img alt="url" src={`${imageBaseUrl}/logo_images/${logoImage[0].s3Name}`} style={{ height: 97 }} />
                ) : (
                  <Logo />
                )}
              </FlexBox>
              <FlexBox height="12.7%" fullWidth align="end" justify="center">
                <Typography
                  color={palette?.primaryText}
                  sx={{ lineHeight: '0.96', fontFamily: fonts?.secondary }}
                  fontSize={100}
                  align="center"
                >
                  CHOOSE THE AMOUNT OF
                  <br />
                  FOOD &amp; RIDE COUPONS
                </Typography>
              </FlexBox>

              <FlexBox height="7%" fullWidth align="end" justify="center">
                <Typography fontSize={35} sx={{ color: '#ED1C24' }} align="center">
                  PLEASE NOTE:
                  <br />
                  THIS YEAR THE COUPONS ARE $1 EACH
                </Typography>
              </FlexBox>
              <FlexBox height="5%" fullWidth align="end" justify="center">
                <Typography
                  color={palette?.primaryText}
                  fontSize={53}
                  sx={{ fontFamily: fonts?.secondary }}
                  align="center"
                >
                  ELIJA LA CANTIDAD DE CUPONES PARA LA COMPRA.
                </Typography>
              </FlexBox>
              <FlexBox
                height="50%"
                fullWidth
                justify="center"
                align="center"
                sx={{
                  display: 'grid',
                  gridTemplateRows: '1fr 1fr 1fr 1fr 1fr',
                  gridAutoFlow: 'column'
                }}
              >
                {/* Price Type Card */}
                {!cart &&
                  offers().map((offer, idx) => (
                    <TicketCard
                      key={idx}
                      disabled={loading || cartReserving}
                      bg={palette?.darkPrimary || ''}
                      textColor={palette?.secondaryText || ''}
                      offer={offer}
                      formattedPrice={formatCurrency(offer.basePrice, { minimumFractionDigits: 0 }, languageSettings)}
                      addToCart={addToCart}
                    />
                  ))}
              </FlexBox>

              <FlexBox height="20%" fullWidth align="start" justify="center" padding="8 0 0">
                <FlexBox fullWidth justify="center">
                  <Typography color={palette?.primaryText} fontSize={42} align="center">
                    DEBIT/CREDIT CARDS ONLY
                  </Typography>
                  <Typography color={palette?.primaryText} fontSize={29} align="center">
                    SOLO TARJETAS DE CRÉDITO DE DÉBITO
                  </Typography>
                </FlexBox>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        )}
      </>
    );
  };

  // Error overlay when reserving tickets fails
  if (cartError) {
    return (
      <ActionOverlay
        flow={flowTemplate}
        style={backgroundStyle()}
        title={
          <Typography variant="h2" align="center">
            {formatText(
              (ticketsUnavailable && ticketsUnavailable.header) || i18n.t('ticketingRoute.ticketsUnavailable'),
              flowTemplate
            )}
          </Typography>
        }
        confirmText={formatText(
          (ticketsUnavailable && ticketsUnavailable.buttons && ticketsUnavailable.buttons[0]) ||
            i18n.t('ticketingRoute.lookOtherTickets'),
          flowTemplate
        )}
        height={660}
        description={
          <Box width={600}>
            <Typography variant="h4" align="center">
              {formatText(
                (ticketsUnavailable && ticketsUnavailable.message) ||
                  i18n.t('saleError.additionalAssistance', { support }),
                flowTemplate
              )}
            </Typography>
          </Box>
        }
        onConfirm={onErrorOverlayConfirm}
      />
    );
  }

  // Loading overlay
  if (loading || uiLoading || cartReserving) {
    return (
      <LoadingOverlay
        message={cartReserving ? formatText(loadingMessage, flowTemplate) : ''}
        style={{ zIndex: '99' }}
      />
    );
  }

  return (
    <>
      {generateTicketSelectionView()}
      {/* actionoverlay when cartRule violate*/}
      {cartRuleViolation && !loading && (
        <ActionOverlay
          title={cartRuleViolation}
          confirmText={t('commonText.close')}
          onConfirm={() => setCartRuleViolation('')}
          height={500}
        />
      )}
    </>
  );
};

export default TicketSelectionView;
